import ControlViewModel from '@/ViewModels/ControlViewModel';
import TabStripViewModel from '@/ViewModels/Controls/TabStripViewModel';
import TabStrip from '@/Controls/TabStrip';
import DetailViewContext from '@/ViewModels/Contexts/DetailViewContext';
import * as CoreFrameDTOs from "coreframetypings/dto";
import TabStripViewModelT from '@/ViewModels/Controls/TabStripViewModel';
import TabStripTabViewModelT from '@/ViewModels/Controls/TabStripTabViewModel';
import LabelViewModelT from '@/ViewModels/Controls/LabelViewModel';
import ComponentManager from '@/Management/ComponentManager';
import GlobalConfiguration from '@/Management/GlobalConfiguration';
import { IWizardGroupBoxViewModelConstructorParameter } from '#/DefaultViewExtensionInterfaces';
import ToolBarViewModel from '@/ViewModels/Controls/ToolBarViewModel';
import WizardChangeTracker from '@/Utility/WizardChangeTracker';
import LocatorHelper from '@/Utility/LocatorHelper';

export default class WizardGroupBoxViewModel extends ControlViewModel {

    /**
    * Enthält den TabStrip
    */
    public tabStrip: TabStripViewModel;

    /**
    * Enthält den TabStrip des Eltern-Elementes, falls diese GroupBox untergeordnet ist.
    */
    public parentTabStrip: TabStrip;

    /**
    * Enthält die inneren DetailViewGroups
    */
    public detailViewGroups: Array<CoreFrameDTOs.IDetailViewGroupDTO>;

    /**
    * Definiert den Namen des inneren Layouts
    */
    public layout: string;

    /**
    * Der Kontext des ViewModels
    */
    public context: DetailViewContext;

    /**
    * Bestimmt den aktiven Tab (index)
    */
    public selectedIndex: KnockoutObservable<number>;

    /**
    * Enthält die DetailViewGroup
    */
    public detailViewGroup: CoreFrameDTOs.IDetailViewGroupDTO;

    public showBrowseButtonsInToolbar: KnockoutObservable<boolean>;

    protected TabStripViewModel = ComponentManager.getViewModel<typeof TabStripViewModelT>('TabStripViewModel');
    protected TabStripTabViewModel = ComponentManager.getViewModel<typeof TabStripTabViewModelT>('TabStripTabViewModel');
    protected LabelViewModel = ComponentManager.getViewModel<typeof LabelViewModelT>('LabelViewModel');

    constructor(params: IWizardGroupBoxViewModelConstructorParameter) {
        super(params);

        this.context = this.paramOrDefault(params.context, null);
        this.layout = this.paramOrDefault(params.layout, 'NoneLayout');
        this.selectedIndex = this.observableOrDefault(params.selectedIndex, 0);
        this.detailViewGroup = this.paramOrDefault(params.detailViewGroup, null);
        this.detailViewGroups = this.paramOrDefault(params.detailViewGroups, []);
        this.showBrowseButtonsInToolbar = this.observableOrDefault(params.showBrowseButtonsInToolbar, false);
        this.tabStrip = this.paramOrDefault(params.tabStrip, () => {
            return new this.TabStripViewModel({
                isWizard: true,
                selectedIndex: this.selectedIndex
            });
        });
        this.parentTabStrip = this.paramOrDefault(params.parentTabStrip, null);

        (() => {
            const changeTracker = new WizardChangeTracker(this.context, this.detailViewGroup);
            changeTracker.track();

            const tab = new this.TabStripTabViewModel({
                parentLocator: this.locator,
                locator: this.detailViewGroup.name,
                context: this.context,
                isWizard: true,
                isEnabled: (this.detailViewGroup as any).isEnabled,
                isVisible: (this.detailViewGroup as any).isVisible,
                detailViewItems: this.detailViewGroup.items,
                detailViewGroups: (() => {
                    const groups = [];
                    if (this.detailViewGroup.detailViewGroups != null) {
                        for (let i = 0; i < this.detailViewGroup.detailViewGroups.length; i++) {
                            const detailViewGroup = this.detailViewGroup.detailViewGroups[i];
                            if (detailViewGroup.uiGenerator.indexOf('WizardGroup') === -1) {
                                groups.push(detailViewGroup);
                            }
                        }
                    }
                    return groups;
                })(),
                layout: this.layout,
                label: new this.LabelViewModel({
                    parentLocator: this.locator,
                    locator: 'wizard-label.' + this.detailViewGroup.name,
                    caption: (this.detailViewGroup as any).caption,
                    ergName: this.detailViewGroup.ergName
                }),
                onBeforeTabChange: async () => {
                    if (this.context == null)
                        return true;

                    // Wenn wir uns in einem gesperrten Kontext befinden, ist eine Bearbeitung nicht möglich.
                    // Daher macht eine Validierung auch keinen Sinn.
                    if (this.context.getRootDetailViewContext().isLocked())
                        return true;

                    const changesInTab = changeTracker.getChanges();

                    // Sind keine Änderungen im aktuellen Tab enthalten, muss nichts validiert werden. Der Tab kann sofort gewechselt werden.
                    if (!changesInTab.detailViewItems.length)
                        return true;

                    try {
                        this.increaseBusyCounter();

                        await this.context.genericObject.whenAllChangesAreCommittedToServer();
                        if (this.isDisposed)
                            return false;

                        const success = await this.context.updateAndValidateProperties(changesInTab.properties);
                        if (this.isDisposed)
                            return false;

                        if (success)
                            changeTracker.remove(changesInTab);

                        // Der Tab soll nur gewechselt werden können, wenn das Update und die Validierung erfolgreich war
                        return success;
                    } finally {
                        if (!this.isDisposed)
                            this.decreaseBusyCounter();
                    }
                }
            });
            this.tabStrip.tabs.push(tab);
        })();
        let initial = true;
        this.defineSubscription(this.showBrowseButtonsInToolbar, (isShowBrowseButtonsInToolbar) => {
            // Nur übergeordnete WirzardGroupBoxen dürfen Navigations-Buttons rendern
            if (this.detailViewGroups.length === 0) {
                return;
            }
            if (isShowBrowseButtonsInToolbar) {
                this._showBrowseButtonsInToolbar();
            } else if (!initial) {
                this.hideBrowseButtonsInToolbar();
            }
            initial = false;
        })(this.showBrowseButtonsInToolbar());
        ControlViewModel.checkProperties(this, params);
    }

    private _showBrowseButtonsInToolbar(): void {
        const toolBar = this.context.getRootDetailViewContext().toolBar;
        if (!toolBar)
            return;

        this.addBrowseNextButton(toolBar)
        this.addBrowsePreviousButton(toolBar);
    }

    private addBrowseNextButton(toolBar: ToolBarViewModel) {
        const ergName = 'WIZARD_NEXT';
        if (toolBar.getElementByErgName(ergName))
            return;

        toolBar.addSecondaryElement({
            ergName: ergName,
            locator: LocatorHelper.buildLocator('Wizard', 'Next'),
            isEnabled: this.defineComputed(() => {
                // Da die Buttons vor den Tabs von Wizard Group gerendert werden, gibt die Funktion "getNextVisibleTabIndex"
                // bei initialer Darstellung immer null zurück.
                // Deswegen wird hier auch geprüft, ob der aktive Tab der erste Tab ist, damit der Button "Weiter"
                // bei der initialen Darstellung aktiv bleibt.
                // Wenn es nur einen Tab gibt, spielt es keine Rolle, weil die Buttons nicht dargestellt werden.
                const selectedIndex = this.selectedIndex();
                const nextVisibleIndex = this.getNextVisibleTabIndex(selectedIndex);
                return nextVisibleIndex != null || selectedIndex === 0;
            }),
            iconUrl: GlobalConfiguration.inlineImages.NAV_RIGHT_CIRCLE,
            iconIsBinary: false,
            onClick: () => {
                let oldIndex = this.selectedIndex();
                let newIndex = this.getNextVisibleTabIndex(oldIndex);
                if (newIndex != null && this.detailViewGroups && newIndex <= this.detailViewGroups.length) {
                    this.handleOnBeforeTabChange(oldIndex, newIndex);
                }
            }
        });
    }

    private addBrowsePreviousButton(toolBar: ToolBarViewModel) {
        const ergName = 'WIZARD_BACK';
        if (toolBar.getElementByErgName(ergName))
            return;

        toolBar.addSecondaryElement({
            ergName: ergName,
            locator: LocatorHelper.buildLocator('Wizard', 'Back'),
            isEnabled: this.defineComputed(() => this.selectedIndex() !== 0),
            iconUrl: GlobalConfiguration.inlineImages.NAV_LEFT_CIRCLE,
            iconIsBinary: false,
            onClick: () => {
                let oldIndex = this.selectedIndex();
                let newIndex = this.getPrevVisibleTabIndex(oldIndex);
                if (newIndex != null && newIndex >= 0) {
                    this.handleOnBeforeTabChange(oldIndex, newIndex);
                }
            }
        });
    }

    private getNextVisibleTabIndex(oldIndex: number): number {
        for (let i = oldIndex + 1; i <= this.tabStrip.tabs.length - 1; i++) {
            if (this.tabStrip.tabs[i].isVisible()) {
                return i;
            }
        }
        return null;
    }

    private getPrevVisibleTabIndex(oldIndex: number): number {
        for (let i = oldIndex - 1; i >= 0; i--) {
            if (this.tabStrip.tabs[i].isVisible()) {
                return i;
            }
        }
        return null;
    }

    private hideBrowseButtonsInToolbar(): void {
        let toolbar = this.context.getRootDetailViewContext().toolBar;
        toolbar.removeSecondaryElementByErgName('WIZARD_NEXT');
        toolbar.removeSecondaryElementByErgName('WIZARD_BACK');
    }

    private handleOnBeforeTabChange(oldIndex: number, newIndex: number): void {
        let aktuell = this.tabStrip.tabs[oldIndex];
        let newTab = this.tabStrip.tabs[newIndex];
        if (newTab && newTab.isVisible()) {
            if (aktuell.onBeforeTabChange) {
                $.when(aktuell.onBeforeTabChange()).then((canChangeTab) => {
                    if (canChangeTab === true) {
                        this.selectedIndex(newIndex);
                    }
                });
            } else {
                this.selectedIndex(newIndex);
            }
        }
    }
}

ComponentManager.setViewModel('WizardGroupBoxViewModel', WizardGroupBoxViewModel, 'RADPresentation.HTMLClient.DefaultViewExtensions');